import { FC, useMemo, useState } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { useTranslation } from 'react-i18next';
import { DailyData } from '../../../common/model/dto/daily-data';
import DateUtils from '../../../common/utils/services/date-utils';
import styled from 'styled-components';
import Variables from '../../../design-system/variables';
import { Skeleton } from '@mui/material';
import OvNoContent from '../molecules/OvNoContent';
import OvUserChartsDaterangeSelector from '../molecules/OvUserChartsDaterangeSelector';
import OvUserChartsRangeSelector from '../molecules/OvUserChartsRangeSelector';
import Colours from '../../../design-system/colours';
import { useQuery } from '@tanstack/react-query';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import { HormoneSummaryResponse } from '../../../common/model/dto/hormone-summary';
import { Paginated } from '../../../common/types';
import { useCycleSelection } from '../../../hooks/use-cycle-selection';
import {
  getScanDayCount,
  MIN_CYCLE_SCAN_DAYS,
} from '../../../common/utils/services/hormone-trends-utils';
import { applyInsufficientScanDataOverride } from '../../../common/utils/services/hormone-summary-utils';
import OvHormoneSummary from './OvHormoneSummary';
import OvHormoneTrends from './OvHormoneTrends';

export interface DailyDataRange {
  startDate: Date;
  endDate: Date;
}

export type FetchDailyData = (range: DailyDataRange) => Promise<DailyData[]>;

interface OvUserChartsProps {
  user?: UserInfo;
  fetchDailyData: FetchDailyData;
  dailyDataQueryKey: unknown[];
  fetchCalendarDailyData: FetchDailyData;
  calendarDailyDataQueryKey: unknown[];
  enabled?: boolean;
  fetchCycles?: (query: string) => Promise<Paginated<UserCycle>>;
  fetchCycleHormoneSummary?: (
    cycleId: string
  ) => Promise<HormoneSummaryResponse>;
  cycleQueryKey?: unknown[];
}

const isValidDate = (date?: Date): boolean =>
  !!date && !Number.isNaN(date.getTime());

const getDateQueryKeyValue = (date?: Date): string | undefined =>
  date && isValidDate(date) ? date.toISOString() : undefined;

const OvUserCharts: FC<OvUserChartsProps> = ({
  user,
  fetchDailyData,
  dailyDataQueryKey,
  fetchCalendarDailyData,
  calendarDailyDataQueryKey,
  enabled = true,
  fetchCycles,
  fetchCycleHormoneSummary,
  cycleQueryKey,
}) => {
  const { t } = useTranslation();
  const [calendarRange, setCalendarRange] = useState<Partial<DailyDataRange>>(
    {}
  );

  const {
    rangeMode,
    customRangeSelection,
    customDateRange,
    dateRange,
    selectedCycle,
    finishedCycles,
    cyclesLoading,
    isRangeInitialized,
    phaseDays,
    summaryViewModel,
    isSummaryLoading,
    shouldRenderHormoneSummary,
    onModeChange,
    onCycleSelect,
    onCustomSelectionChange,
    onSummaryRetry,
  } = useCycleSelection(
    user,
    fetchCycles,
    fetchCycleHormoneSummary,
    cycleQueryKey
  );

  const isDateRangeValid =
    isValidDate(dateRange.startDate) && isValidDate(dateRange.endDate);
  const isSelectedRangeReady =
    isRangeInitialized && (rangeMode === 'custom' || !!selectedCycle);

  const { data: dailyDataArray, isFetching } = useQuery({
    queryKey: [
      ...dailyDataQueryKey,
      getDateQueryKeyValue(dateRange.startDate),
      getDateQueryKeyValue(dateRange.endDate),
    ],
    queryFn: () =>
      fetchDailyData({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    enabled: enabled && !!user && isDateRangeValid && isSelectedRangeReady,
  });

  const { data: dailyDataInCalendar = [] } = useQuery({
    queryKey: [
      ...calendarDailyDataQueryKey,
      getDateQueryKeyValue(calendarRange.startDate),
      getDateQueryKeyValue(calendarRange.endDate),
    ],
    queryFn: () =>
      fetchCalendarDailyData({
        startDate: calendarRange.startDate!,
        endDate: calendarRange.endDate!,
      }),
    enabled:
      !!user &&
      enabled &&
      isValidDate(calendarRange.startDate) &&
      isValidDate(calendarRange.endDate),
  });

  const isLoading = !isRangeInitialized || (isFetching && !dailyDataArray);

  const scanDayCount = useMemo(
    () => getScanDayCount(dailyDataArray ?? []),
    [dailyDataArray]
  );
  const hasInsufficientScanData =
    rangeMode === 'cycle' && scanDayCount < MIN_CYCLE_SCAN_DAYS;
  const effectivePhaseDays = hasInsufficientScanData ? {} : phaseDays;
  const effectiveSummaryViewModel =
    hasInsufficientScanData && summaryViewModel
      ? applyInsufficientScanDataOverride(summaryViewModel)
      : summaryViewModel;

  const onCalendarRangeChange = (startDate: Date, endDate: Date) => {
    setCalendarRange({ startDate, endDate });
  };

  const selectedRangeText = `${DateUtils.formatLocaleDate(
    dateRange.startDate
  )} - ${DateUtils.formatLocaleDate(dateRange.endDate)}`;

  return (
    <>
      <OverViewWrapper>
        {user && (
          <Container>
            {fetchCycles ? (
              <OvUserChartsRangeSelector
                mode={rangeMode}
                onModeChange={onModeChange}
                cycles={finishedCycles}
                cyclesLoading={cyclesLoading}
                selectedCycleId={selectedCycle?.id ?? ''}
                onCycleSelect={onCycleSelect}
                customSelection={customRangeSelection}
                customDateRange={customDateRange}
                onCustomSelectionChange={onCustomSelectionChange}
                customRangeText={selectedRangeText}
                dailyDataInCalendar={dailyDataInCalendar}
                onCalendarRangeChange={onCalendarRangeChange}
              />
            ) : (
              <OvUserChartsDaterangeSelector
                selection={customRangeSelection}
                defaultText={selectedRangeText}
                dateRange={customDateRange}
                onSelectionChange={onCustomSelectionChange}
                dailyDataInCalendar={dailyDataInCalendar}
                onCalendarRangeChange={onCalendarRangeChange}
              />
            )}

            {shouldRenderHormoneSummary && selectedCycle && (
              <HormoneSummaryWrapper>
                {effectiveSummaryViewModel ? (
                  <OvHormoneSummary
                    summary={effectiveSummaryViewModel}
                    selectedCycle={selectedCycle}
                  />
                ) : isSummaryLoading ? (
                  <OvHormoneSummary state="loading" />
                ) : (
                  <OvHormoneSummary state="error" onRetry={onSummaryRetry} />
                )}
              </HormoneSummaryWrapper>
            )}

            {isLoading && (
              <ContentWrapper
                aria-busy="true"
                aria-label={t('hormoneTrends.loading')}
              >
                <ChartContainer>
                  <ChartLoadingContent>
                    <Skeleton variant="text" width="14rem" height="2.5rem" />
                    <ChartLoadingSection>
                      <Skeleton variant="rectangular" height="18rem" />
                      <Skeleton variant="text" width="12rem" />
                      <Skeleton variant="text" width="10rem" />
                    </ChartLoadingSection>
                  </ChartLoadingContent>
                </ChartContainer>
              </ContentWrapper>
            )}

            {!isLoading && dailyDataArray && (
              <ContentWrapper>
                <ChartContainer>
                  <ChartContent>
                    <OvHormoneTrends
                      dateRange={dateRange}
                      rangeMode={rangeMode}
                      selectedCycle={selectedCycle}
                      dailyDataArray={dailyDataArray}
                      phaseDays={effectivePhaseDays}
                      user={user}
                    />
                  </ChartContent>
                </ChartContainer>
              </ContentWrapper>
            )}
          </Container>
        )}
        {isRangeInitialized && !isLoading && !dailyDataArray && (
          <OvNoContent>{t('dashboard.noContent')}</OvNoContent>
        )}
      </OverViewWrapper>
    </>
  );
};

export default OvUserCharts;

const OverViewWrapper = styled.div`
  display: 'flex';
  flex-direction: 'row';
  flex-wrap: 'nowrap';
  width: 100%;
`;

const Container = styled.div``;

const HormoneSummaryWrapper = styled.div`
  margin: 1rem 0;
`;

const ChartContainer = styled.div`
  box-shadow: ${Variables.boxShadow.defaultBox};
  border-radius: ${Variables.borderRadius.SMALL};
  width: 100%;
`;

const ChartContent = styled.div`
  padding: 2rem 2.5rem;
`;

const ChartLoadingContent = styled.div`
  padding: 1.5rem 2.5rem;
`;

const ChartLoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  &:last-child {
    border-bottom: 0;
  }

  .MuiSkeleton-rectangular {
    border-radius: ${Variables.borderRadius.SMALL};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
